import type { Dispatch, SetStateAction } from 'react';

import {
  ApiClientError,
  ApiClientTransportError,
  type FolioChatRequest,
} from '@contract-analyzer/api-client';

import { toApiPageContext } from '@/hooks/folio-page-context-contract';
import {
  applyStreamEvent,
  createMessage,
  finalizeAssistantMessage,
  sanitizeErrorMessage,
} from '@/hooks/folio-message-state';
import { decodeFolioEvent } from '@/hooks/folio-event-decoder';
import { readSseResponse } from '@/lib/sse-reader';
import type {
  FolioMode,
  FolioStreamMessage,
  LastRequest,
  PageContext,
} from '@/hooks/folio-types';

type FolioChatFn = (
  input: FolioChatRequest,
  options?: { signal?: AbortSignal },
) => Promise<Response>;

interface WritableRef<T> {
  current: T;
}

function isAbortError(cause: unknown): boolean {
  if (cause instanceof Error && cause.name === 'AbortError') {
    return true;
  }
  if (cause instanceof ApiClientTransportError) {
    const inner: unknown = cause.cause;
    return inner instanceof Error && inner.name === 'AbortError';
  }
  return false;
}

function buildStreamErrorMessage(cause: unknown): string {
  if (cause instanceof ApiClientTransportError) {
    const inner: unknown = cause.cause;
    if (inner instanceof Error && inner.message.trim().length > 0) {
      return sanitizeErrorMessage(inner.message);
    }
    return 'Sean lost the connection.';
  }
  if (cause instanceof ApiClientError) {
    const { status } = cause;
    if (status === 401 || status === 403) {
      return 'Sean could not authenticate this request.';
    }
    if (status === 429) {
      return 'Sean is temporarily busy. Please wait a moment and try again.';
    }
    if (status === 404) {
      return 'Sean is not available yet.';
    }
    return 'Sean could not complete the request.';
  }
  if (cause instanceof Error && cause.message.trim().length > 0) {
    return sanitizeErrorMessage(cause.message);
  }
  return 'Sean lost the connection.';
}

interface StartMessageStreamOptions {
  readonly folioChat: FolioChatFn;
  readonly abortActiveStream: () => void;
  readonly setMessages: Dispatch<SetStateAction<readonly FolioStreamMessage[]>>;
  readonly setIsStreaming: Dispatch<SetStateAction<boolean>>;
  readonly setCurrentMode: Dispatch<SetStateAction<FolioMode | null>>;
  readonly setError: Dispatch<SetStateAction<string | null>>;
  readonly currentModeRef: WritableRef<FolioMode | null>;
  readonly abortControllerRef: WritableRef<AbortController | null>;
  readonly activeAssistantMessageIdRef: WritableRef<string | null>;
  readonly activeStreamIdRef: WritableRef<string | null>;
  readonly isStreamingRef: WritableRef<boolean>;
  readonly lastRequestRef: WritableRef<LastRequest | null>;
}

export async function startFolioMessageStream(
  {
  folioChat,
  abortActiveStream,
  setMessages,
  setIsStreaming,
  setCurrentMode,
  setError,
  currentModeRef,
  abortControllerRef,
  activeAssistantMessageIdRef,
  activeStreamIdRef,
  isStreamingRef,
  lastRequestRef,
}: StartMessageStreamOptions,
  trimmedText: string,
  conversationId: string | null,
  pageContext: PageContext | null,
  updateMessages: (
    currentMessages: readonly FolioStreamMessage[],
  ) => readonly FolioStreamMessage[] = (currentMessages) => currentMessages,
) {
  abortActiveStream();
  setError(null);

    const modeSnapshot = currentModeRef.current;
    const userMessage = createMessage('user', trimmedText, modeSnapshot, false, {
      sourceRole: 'user',
    });
    const assistantMessage = createMessage('assistant', '', modeSnapshot, true, {
      sourceRole: 'assistant',
    });
    const requestSnapshot: LastRequest = {
      text: trimmedText,
      conversationId,
      pageContext,
      userMessageId: userMessage.id,
      assistantMessageId: assistantMessage.id,
    };
    const streamId = crypto.randomUUID();
    const controller = new AbortController();
    let hasStartedStreamingResponse = false;
    let streamFinishedSuccessfully = false;
    let preserveAssistantAfterFailure = false;

    lastRequestRef.current = requestSnapshot;
    abortControllerRef.current = controller;
    activeAssistantMessageIdRef.current = assistantMessage.id;
    activeStreamIdRef.current = streamId;
    setMessages((currentMessages) => [
      ...updateMessages(currentMessages),
      userMessage,
      assistantMessage,
    ]);
    setIsStreaming(true);
    isStreamingRef.current = true;

    const chatRequest: FolioChatRequest = {
      message: trimmedText,
      ...(conversationId === null ? {} : { conversation_id: conversationId }),
      ...(pageContext === null ? {} : { page_context: toApiPageContext(pageContext) }),
    };

    try {
      const response = await folioChat(chatRequest, { signal: controller.signal });

      const events = readSseResponse(response, controller.signal);
      hasStartedStreamingResponse = true;
      let streamMode = modeSnapshot;
      for await (const frame of events) {
        if (activeStreamIdRef.current !== streamId) return;
        const event = decodeFolioEvent(frame.event ?? null, frame.data);
        if (event === null) {
          if (['text_delta', 'error', 'done'].includes(frame.event ?? '')) throw new Error('Sean returned an invalid response.');
          continue;
        }
        if (event.type === 'error') {
          throw new Error(sanitizeErrorMessage(event.message));
        }
        if (event.type === 'mode_change') {
          streamMode = event.mode;
          setCurrentMode(event.mode);
        }
        setMessages((currentMessages) =>
          applyStreamEvent(currentMessages, assistantMessage.id, event, streamMode),
        );
        if (event.type === 'done') {
          streamFinishedSuccessfully = true;
          break;
        }
      }
      if (!streamFinishedSuccessfully) throw new Error('Sean lost the connection before completing the response.');
    } catch (cause: unknown) {
      if (isAbortError(cause)) {
        return;
      }

      if (activeStreamIdRef.current === streamId) {
        preserveAssistantAfterFailure = hasStartedStreamingResponse;
        setError(buildStreamErrorMessage(cause));
      }
    } finally {
      if (activeStreamIdRef.current === streamId) {
        abortControllerRef.current = null;
        activeStreamIdRef.current = null;
        activeAssistantMessageIdRef.current = null;
        setIsStreaming(false);
        isStreamingRef.current = false;
        setMessages((currentMessages) =>
          finalizeAssistantMessage(
            currentMessages,
            assistantMessage.id,
            preserveAssistantAfterFailure,
          ),
        );
        if (streamFinishedSuccessfully) {
          lastRequestRef.current = null;
        }
      }
    }
}
